import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AndonBoardLayoutItem,
  AndonCellBorder,
  AndonGridCell,
  BorderSide,
  DEFAULT_CELL_BORDER_COLOR
} from "~/features/andon/andon.types";
import {
  cellsInRect,
  clientPointToAndonGridCell
} from "~/features/andon/andon.utils";
import {
  canStartMarqueeFromMouseDown,
  createSkipNextGridClickGuard,
  getPickerSelectionTitle,
  getSelectionHighlightCells,
  hasExceededMarqueeDragThreshold,
  isBackdropClickWithClientPoint,
  isTextLabelSelection,
  layoutItemAtCell,
  menuOpenCellKeys,
  resolveTargetCell,
  shouldReopenEditMenuForClickedCell
} from "~/features/andon/andonBoard/andonBoardEdit.utils";
import { AndonWorkstation } from "~/types/api";

type UseAndonBoardEditSelectionParams = {
  editModeEnabled: boolean;
  advanced: boolean;
  boardWidth?: number;
  boardHeight?: number;
  boardDimensions: { columns: number; rows: number };
  gridRowHeight: number;
  workingLayoutItems: AndonBoardLayoutItem[];
  cellBorders: AndonCellBorder[];
  workstations?: AndonWorkstation[];
};

export function useAndonBoardEditSelection({
  editModeEnabled,
  advanced,
  boardWidth,
  boardHeight,
  boardDimensions,
  gridRowHeight,
  workingLayoutItems,
  cellBorders,
  workstations
}: UseAndonBoardEditSelectionParams) {
  const [selectedGridIndex, setSelectedGridIndex] = useState<string | null>(
    null
  );
  const [selectedCells, setSelectedCells] = useState<AndonGridCell[]>([]);
  const [marqueePreviewCells, setMarqueePreviewCells] = useState<
    AndonGridCell[]
  >([]);
  const [isMarqueeDragging, setIsMarqueeDragging] = useState(false);
  const marqueeStartRef = useRef<{
    cell: AndonGridCell;
    clientX: number;
    clientY: number;
  } | null>(null);
  const marqueeActiveRef = useRef(false);
  const { skipNextGridClickRef, armSkipNextGridClick, clearSkipNextGridClick } =
    useRef(createSkipNextGridClickGuard()).current;
  const [editCellMenuAnchorPosition, setEditCellMenuAnchorPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const isEditCellMenuOpen = Boolean(editCellMenuAnchorPosition);

  const resolveClientToCell = useCallback(
    (clientX: number, clientY: number): AndonGridCell | null => {
      const grid = document.querySelector(".andon-board");
      if (!grid || !boardWidth || !boardHeight) {
        return null;
      }
      return clientPointToAndonGridCell({
        clientX,
        clientY,
        gridElement: grid,
        columns: boardDimensions.columns,
        rows: boardDimensions.rows,
        rowPitch: gridRowHeight,
        fallbackWidth: boardWidth
      });
    },
    [
      boardWidth,
      boardHeight,
      boardDimensions.columns,
      boardDimensions.rows,
      gridRowHeight
    ]
  );

  const openEditCellMenuAt = (clientX: number, clientY: number) => {
    setEditCellMenuAnchorPosition({ left: clientX, top: clientY });
  };

  const selectSingleCell = (
    cell: AndonGridCell,
    options?: {
      tileId?: string | null;
      clientX?: number;
      clientY?: number;
    }
  ) => {
    setSelectedCells([cell]);
    setSelectedGridIndex(options?.tileId ?? null);
    setMarqueePreviewCells([]);
    if (options?.clientX != null && options?.clientY != null) {
      openEditCellMenuAt(options.clientX, options.clientY);
    }
  };

  const clearEditCellMenu = () => {
    setSelectedGridIndex(null);
    setEditCellMenuAnchorPosition(null);
    setMarqueePreviewCells([]);
    setSelectedCells([]);
  };

  const closeEditCellMenu = (
    event?: object | React.SyntheticEvent | Event | null,
    reason?: string
  ) => {
    if (isBackdropClickWithClientPoint(advanced, reason, event)) {
      const { clientX, clientY } = event;
      const clickedCell = resolveClientToCell(clientX, clientY);
      if (clickedCell) {
        const anchorCell = editCellMenuAnchorPosition
          ? resolveClientToCell(
              editCellMenuAnchorPosition.left,
              editCellMenuAnchorPosition.top
            )
          : null;
        const openKeys = menuOpenCellKeys({
          selectedCells,
          selectedGridIndex,
          workingLayoutItems,
          anchorCell
        });
        if (shouldReopenEditMenuForClickedCell(openKeys, clickedCell)) {
          const tile = layoutItemAtCell(workingLayoutItems, clickedCell);
          selectSingleCell(clickedCell, {
            tileId: tile?.i ?? null,
            clientX,
            clientY
          });
          return;
        }
      }
    }
    clearEditCellMenu();
  };

  const handleGridClick = (event: MouseEvent) => {
    if (!editModeEnabled) return;
    if (skipNextGridClickRef.current) {
      clearSkipNextGridClick();
      return;
    }

    const cell = resolveClientToCell(event.clientX, event.clientY);
    if (!cell) {
      return;
    }

    selectSingleCell(cell, {
      tileId: null,
      clientX: event.clientX,
      clientY: event.clientY
    });
  };

  const handleBoardMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (
      !canStartMarqueeFromMouseDown(event, {
        editModeEnabled,
        advanced
      })
    ) {
      return;
    }
    const cell = resolveClientToCell(event.clientX, event.clientY);
    if (!cell) {
      return;
    }
    marqueeStartRef.current = {
      cell,
      clientX: event.clientX,
      clientY: event.clientY
    };
    marqueeActiveRef.current = false;
  };

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!editModeEnabled || !advanced) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setSelectedCells([]);
      clearSkipNextGridClick();
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setMarqueePreviewCells([]);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setIsMarqueeDragging(false);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      if (!editModeEnabled) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setSelectedGridIndex(null);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setEditCellMenuAnchorPosition(null);
      }
    }
  }, [editModeEnabled, advanced, clearSkipNextGridClick]);

  useEffect(() => {
    if (!advanced) {
      return;
    }
    const onWindowMouseUp = (event: MouseEvent) => {
      const start = marqueeStartRef.current;
      const wasMarquee = marqueeActiveRef.current;
      if (!start) {
        return;
      }
      marqueeStartRef.current = null;
      marqueeActiveRef.current = false;
      setIsMarqueeDragging(false);
      setMarqueePreviewCells([]);
      if (!wasMarquee || !editModeEnabled) {
        return;
      }
      const endCell =
        resolveClientToCell(event.clientX, event.clientY) ?? start.cell;
      const range = cellsInRect(start.cell, endCell);
      setSelectedCells(range);
      setSelectedGridIndex(null);
      openEditCellMenuAt(event.clientX, event.clientY);
      armSkipNextGridClick();
    };
    const onWindowMouseMove = (event: MouseEvent) => {
      const start = marqueeStartRef.current;
      if (!start || !editModeEnabled) {
        return;
      }
      const dx = event.clientX - start.clientX;
      const dy = event.clientY - start.clientY;
      if (!marqueeActiveRef.current) {
        if (!hasExceededMarqueeDragThreshold(dx, dy)) {
          return;
        }
        marqueeActiveRef.current = true;
        setIsMarqueeDragging(true);
      }
      const endCell = resolveClientToCell(event.clientX, event.clientY);
      if (!endCell) {
        return;
      }
      setMarqueePreviewCells(cellsInRect(start.cell, endCell));
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    window.addEventListener("mousemove", onWindowMouseMove);
    return () => {
      window.removeEventListener("mouseup", onWindowMouseUp);
      window.removeEventListener("mousemove", onWindowMouseMove);
    };
  }, [editModeEnabled, advanced, resolveClientToCell, armSkipNextGridClick]);

  const isTextLabelSelected = isTextLabelSelection(
    selectedGridIndex,
    workingLayoutItems
  );
  const isMultiCellSelection = selectedCells.length > 1;
  const selectionHighlightCells = getSelectionHighlightCells({
    marqueePreviewCells,
    selectedCells
  });

  const anchorCellFromMenu = useMemo(() => {
    if (!editCellMenuAnchorPosition) {
      return null;
    }
    return resolveClientToCell(
      editCellMenuAnchorPosition.left,
      editCellMenuAnchorPosition.top
    );
  }, [editCellMenuAnchorPosition, resolveClientToCell]);

  const targetCell = useMemo(
    () =>
      resolveTargetCell({
        selectedCells,
        selectedGridIndex,
        workingLayoutItems,
        anchorCell: anchorCellFromMenu
      }),
    [selectedCells, selectedGridIndex, workingLayoutItems, anchorCellFromMenu]
  );

  const borderTargetCells = useMemo(() => {
    if (selectedCells.length > 0) {
      return selectedCells;
    }
    return targetCell ? [targetCell] : [];
  }, [selectedCells, targetCell]);

  const targetCellBorder =
    borderTargetCells.length === 1
      ? cellBorders.find(
          (cellBorder) =>
            cellBorder.x === borderTargetCells[0].x &&
            cellBorder.y === borderTargetCells[0].y
        )
      : undefined;
  const targetCellSides: BorderSide[] = targetCellBorder?.sides ?? [];
  const targetCellColor = targetCellBorder?.color ?? DEFAULT_CELL_BORDER_COLOR;

  const pickerSelectionTitle = useMemo(
    () =>
      getPickerSelectionTitle({
        selectedGridIndex,
        workstations,
        targetCell
      }),
    [selectedGridIndex, workstations, targetCell]
  );

  return {
    selectedGridIndex,
    setSelectedGridIndex,
    selectedCells,
    marqueePreviewCells,
    isMarqueeDragging,
    editCellMenuAnchorPosition,
    isEditCellMenuOpen,
    isMultiCellSelection,
    selectionHighlightCells,
    targetCell,
    borderTargetCells,
    targetCellSides,
    targetCellColor,
    pickerSelectionTitle,
    isTextLabelSelected,
    resolveClientToCell,
    selectSingleCell,
    clearEditCellMenu,
    closeEditCellMenu,
    handleGridClick,
    handleBoardMouseDown
  };
}
